import React, {CSSProperties, ReactElement, type ReactNode} from 'react';
import {Brand, Motion, Radii, Shadows} from "../../../colors";

type ButtonProps = {
    children?: ReactNode;
    loading?: boolean;
    title?: string;
    error?: boolean;
    additionalClassName?: string;
    overrideClassName?: string;
    onClick?: () => void;
    style?: CSSProperties;
    color?: "light" | "dark";
    type?: "submit" | "button" | "reset" | undefined;
    size?: "small" | "medium" | "wide" | undefined;
    visibility?: string;
    disabled?: boolean;
}

const SIZE_WIDTHS: Record<NonNullable<ButtonProps["size"]>, string> = {
    small:  "w-1/4",
    medium: "w-1/2",
    wide:   "w-full",
};

const COLOR_VARIANTS = {
    dark:  `${Brand.solid} ${Brand.solidHover} ${Shadows.sm} hover:shadow-[0_8px_24px_-8px_rgba(106,78,170,0.35)]`,
    light: "bg-[#fafafb] text-[#15151b] hover:bg-[#f4f4f6] border border-[#e8e8ec]",
};

const Button: React.FC<ButtonProps> = ({
    children,
    loading = false,
    title,
    error = false,
    additionalClassName = "",
    overrideClassName = "",
    onClick,
    style,
    color = "dark",
    type = "submit",
    size = "medium",
    visibility = " visible ",
    disabled = false,
}: ButtonProps): ReactElement => {
    const baseClasses = [
        "flex-1 items-center cursor-pointer font-semibold py-2.5 px-4 mb-6",
        Radii.md,
        Motion.base,
        Brand.ring,
        SIZE_WIDTHS[size ?? "medium"],
        error ? "mt-6" : "",
        COLOR_VARIANTS[color],
        (loading || disabled) ? "opacity-60 cursor-not-allowed" : "",
        additionalClassName,
    ].filter(Boolean).join(" ");

    const className = overrideClassName || baseClasses;

    return (
        <div style={style} className="justify-center">
            <button
                type={type}
                className={className + visibility}
                onClick={onClick}
                disabled={loading || disabled}
            >
                <div className="flex items-center justify-center">
                    {loading && (
                        <svg
                            className={"items-center justify-center animate-spin -ml-1 mr-3 h-5 w-5 text-white" + visibility}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                        </svg>
                    )}
                    {title}
                    {children}
                </div>
            </button>
        </div>
    );
};

export default Button;
